/**
 * 🎯 TodayGoal — single auto-picked daily target on /pet.
 *
 * Polls /api/goals/today every 60s. Shows a progress bar, the reward, and
 * a Claim button when complete. Auto-rotates at PT midnight (backend logic).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TodayGoal() {
  const [goal, setGoal] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/goals/today`);
      setGoal(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  const onClaim = async () => {
    if (!goal || !goal.complete || goal.claimed || claiming) return;
    setClaiming(true);
    try {
      const r = await axios.post(`${API}/goals/today/claim`);
      const reward = r.data?.reward || {};
      const bits = [];
      if (reward.spirit) bits.push(`+${reward.spirit} 🍚`);
      if (reward.od) bits.push(`+${reward.od} 🪙`);
      if (reward.tokens) bits.push(`+${reward.tokens} 🔥`);
      toast.success(`🎯 Goal claimed! ${bits.join(" · ")}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setClaiming(false);
    }
  };

  if (!goal) return null;

  const pct = Math.min(100, Math.round((goal.progress / Math.max(1, goal.target)) * 100));
  const rewardBits = [];
  if (goal.reward?.spirit) rewardBits.push(`+${goal.reward.spirit} 🍚`);
  if (goal.reward?.od) rewardBits.push(`+${goal.reward.od} 🪙`);
  if (goal.reward?.tokens) rewardBits.push(`+${goal.reward.tokens} 🔥`);

  const fullyDone = goal.claimed;
  return (
    <div
      data-testid="today-goal"
      className={`mb-6 border-2 p-4 ${
        fullyDone
          ? "border-zinc-900 bg-neutral-950 opacity-70"
          : goal.complete
            ? "border-[#FCD34D] bg-gradient-to-r from-amber-950/40 via-amber-950/20 to-transparent"
            : "border-[#7C3AED]/50 bg-gradient-to-r from-purple-950/30 via-purple-950/15 to-transparent"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="text-3xl leading-none shrink-0">{goal.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C4B5FD] font-bold">🎯 Today's Goal</div>
              <div className="font-display text-lg leading-tight mt-0.5">{goal.title}</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono tabular-nums shrink-0">
              {goal.progress} / {goal.target}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-2 h-2 bg-black border border-zinc-900 overflow-hidden">
            <div
              className={`h-full transition-all ${
                fullyDone
                  ? "bg-neutral-700"
                  : goal.complete
                    ? "bg-gradient-to-r from-[#D4AF37] to-[#FCD34D]"
                    : "bg-gradient-to-r from-[#7C3AED] to-[#A78BFA]"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
            <div className="text-[11px] text-neutral-400">
              Reward: <span className="font-bold text-[#FCD34D]">{rewardBits.join(" · ") || "—"}</span>
            </div>
            <button
              onClick={onClaim}
              disabled={!goal.complete || goal.claimed || claiming}
              data-testid="today-goal-claim"
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition shrink-0 ${
                goal.claimed
                  ? "bg-zinc-900 text-neutral-600 cursor-not-allowed"
                  : goal.complete
                    ? "bg-[#D4AF37] hover:bg-[#F1C84B] text-black"
                    : "bg-neutral-900 text-neutral-600 cursor-not-allowed"
              }`}
            >
              {goal.claimed ? "✓ Claimed" : goal.complete ? (claiming ? "Claiming…" : "🎯 Claim") : "In Progress"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
